import { useEffect } from 'react';
import {
  ImageList,
  ImageListItem,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectGameDetailWithAffiliates } from '../store/selectors/games-selectors';
import { loadGameDetail } from '../store/thunks/games-thunks';
import { AdSlot } from '../components/AdSlot';
import { AffiliateLinks } from '../components/AffiliateLinks';
import { ApiUnavailableBanner } from '../components/ApiUnavailableBanner';
import { GameHero } from '../components/GameHero';
import { GameMediaClips } from '../components/GameMediaClips';
import { GameMetadata } from '../components/GameMetadata';
import { GamePurchaseLinks } from '../components/GamePurchaseLinks';
import { GameReleases } from '../components/GameReleases';
import { RecentCoverage } from '../components/RecentCoverage';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import type {
  GameAffiliateLink,
  GameDetail,
  GameMediaItem,
} from '../lib/api/games-api-client';

/**
 * Game-page surface (round-26 unified rewrite).
 *
 * Layout (top to bottom):
 *   1. `<GameHero>` — 16:9 landscape banner with gradient + title +
 *      earliest-release-date chip + platform icons overlaid.
 *   2. `<GameMetadata>` — developer / publisher / genre chip rows.
 *   3. `<GameReleases>` — detailed per-platform launch grid (hidden
 *      when all platforms share one date; the hero overlay already
 *      covers that case).
 *   4. `<GamePurchaseLinks>` — "Where to buy" button strip.
 *   5. `<AffiliateLinks>` — legacy pre-round-26 Amazon affiliate
 *      block, kept rendering when `affiliateLinks` is non-empty
 *      (the legacy wire path still populates it for release records).
 *   6. Summary paragraph (agent-written).
 *   7. `<AdSlot slot={1}>`.
 *   8. Gallery `<ImageList>`.
 *   9. `<GameMediaClips>` (round-27 reserved).
 *  10. `<RecentCoverage>` — News records that mention this game.
 *  11. `<AdSlot slot={2}>`.
 */

function toAffiliateLinkProps(
  link: GameAffiliateLink,
): { readonly url: string; readonly label: string } {
  return { url: link.url, label: link.vendor };
}

interface GalleryProps {
  readonly images: ReadonlyArray<GameMediaItem>;
}

/** Gallery `<ImageList>` — one tile per image. Returns `null` on empty. */
function Gallery({ images }: GalleryProps): JSX.Element | null {
  if (images.length === 0) return null;
  return (
    <ImageList aria-label="Gallery" cols={3} gap={8} sx={{ my: 0 }}>
      {images.map((img, idx) => (
        <ImageListItem key={`img-${idx}`}>
          <img src={img.url} alt={`Gallery image ${idx + 1}`} loading="lazy" />
        </ImageListItem>
      ))}
    </ImageList>
  );
}

interface GameDetailLayoutProps {
  readonly detail: GameDetail;
}

/** Full Game-page layout. */
function GameDetailLayout({ detail }: GameDetailLayoutProps): JSX.Element {
  const affiliateProps = detail.affiliateLinks.map(toAffiliateLinkProps);
  return (
    <Stack spacing={3}>
      <GameHero
        title={detail.title}
        hero={detail.media.hero}
        releaseDate={detail.releaseDate}
        platforms={detail.platforms}
      />
      <GameMetadata
        developers={detail.developers}
        publishers={detail.publishers}
        genres={detail.genres}
      />
      <GameReleases releases={detail.releases} />
      <GamePurchaseLinks links={detail.purchaseLinks} />
      {affiliateProps.length > 0 && <AffiliateLinks links={affiliateProps} />}
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{detail.summary}</Typography>
      <AdSlot slot={1} />
      <Gallery images={detail.media.gallery} />
      <GameMediaClips clips={detail.media.clips} />
      <RecentCoverage articleUuids={detail.articlesMentionedUuids} />
      <AdSlot slot={2} />
    </Stack>
  );
}

/** Description-truncation length for the per-game `<meta description>`. */
const DESCRIPTION_MAX_CHARS = 200;

function truncateSummary(summary: string): string {
  if (summary.length <= DESCRIPTION_MAX_CHARS) return summary;
  return `${summary.slice(0, DESCRIPTION_MAX_CHARS).trimEnd()}…`;
}

export default function GamePage(): JSX.Element {
  const params = useParams<{ gameId: string }>();
  const gameId = params.gameId ?? '';
  const dispatch = useAppDispatch();
  const entry = useAppSelector((s) =>
    gameId ? selectGameDetailWithAffiliates(s, gameId) : undefined,
  );
  useEffect(() => {
    if (gameId) void dispatch(loadGameDetail(gameId));
  }, [dispatch, gameId]);
  return (
    <>
      <GamePageSeoHead gameId={gameId} detail={entry?.data} />
      {renderGameBody({ gameId, entry })}
    </>
  );
}

interface GamePageSeoHeadProps {
  readonly gameId: string;
  readonly detail: GameDetail | undefined;
}

/** Per-record `<SeoHead>` for the Game-page route. */
function GamePageSeoHead({ gameId, detail }: GamePageSeoHeadProps): JSX.Element {
  const route = gameId ? `/games/${gameId}` : '/games';
  if (detail === undefined) {
    return (
      <SeoHead
        route={route}
        branding={brandingFixture.share}
        jsonLdType="VideoGame"
      />
    );
  }
  return (
    <SeoHead
      route={route}
      pageTitle={detail.title}
      pageDescription={truncateSummary(detail.summary)}
      pageImage={detail.media.hero.url.length > 0 ? detail.media.hero.url : undefined}
      branding={brandingFixture.share}
      jsonLdType="VideoGame"
      jsonLdData={{
        name: detail.title,
        image: detail.media.hero.url.length > 0 ? detail.media.hero.url : undefined,
        datePublished: detail.releaseDate,
      }}
    />
  );
}

interface GameBodyArgs {
  readonly gameId: string;
  readonly entry: ReturnType<typeof selectGameDetailWithAffiliates> | undefined;
}

/** "Coming soon" fallback rendered when the live `GetGameByIdV1` returns no record. */
function GameComingSoon({ gameId }: { readonly gameId: string }): JSX.Element {
  const title = gameId
    .split(/[-_]+/)
    .map((part) => (part.length > 0 ? part[0]?.toUpperCase() + part.slice(1) : part))
    .join(' ');
  return (
    <Stack spacing={2}>
      <Typography variant="h1" component="h1">{title}</Typography>
      <Typography variant="body1" color="text.secondary">
        Detailed information for this game is coming soon. In the meantime,
        check the Daily page for the latest releases, news, and facts.
      </Typography>
      <MuiLink href="/">Back to Daily</MuiLink>
    </Stack>
  );
}

function renderGameBody(args: GameBodyArgs): JSX.Element {
  if (!args.gameId) return <ApiUnavailableBanner />;
  if (args.entry?.status === 'failed') return <GameComingSoon gameId={args.gameId} />;
  if (!args.entry?.data) return <Typography>Loading…</Typography>;
  return <GameDetailLayout detail={args.entry.data} />;
}
